import React, { useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { groupBy, orderBy } from "lodash";

import { AssetClassId } from "@fartherfinance/frontend/api/Investment/types";
import { Ticker } from "@fartherfinance/frontend/api/Types";

import { usePortfolioBuilderContext } from "../PortfolioBuilder/reducer/Context";
import {
  AssetClassDropdownOption,
  ElementID,
  ModelTableRowType,
} from "../PortfolioBuilder/reducer/Types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./ReAssignAssetClassModal.module.css";

interface Sleeve {
  id: ElementID;
  type: Extract<ModelTableRowType, "Sleeve">;
  name: string;
}

interface Security {
  id: ElementID;
  type: Extract<ModelTableRowType, "Security">;
  ticker: Ticker;
  description: string;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    elementId: ElementID,
    fromAssetClassId: AssetClassId,
    toAssetClassElementId: ElementID
  ) => void;
  element: Sleeve | Security;
  currentAssetClassId: AssetClassId;
};

const ReAssignAssetClassModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  element,
  currentAssetClassId,
}): JSX.Element => {
  const [assetClassToAssign, setAssetClassToAssign] = useState<
    AssetClassDropdownOption | undefined
  >(undefined);

  const onSelectAssetClassToAssign = (ac: DropdownMenuOption<ElementID>) =>
    setAssetClassToAssign(ac);

  const { state } = usePortfolioBuilderContext();

  const onSave = () => {
    if (assetClassToAssign?.id === undefined) {
      return;
    }

    onSubmit(element.id, currentAssetClassId, assetClassToAssign.id);
  };

  const assetClassesInModel = useMemo(() => {
    const assetClasses = state.modelTable
      .filter((ac) => ac.assetClassId !== currentAssetClassId)
      .map((ac) => ({
        id: ac.id,
        name: ac.name,
        category: ac.category,
      }));

    const groupedByCategory = groupBy(assetClasses, (c) => c.category);

    return orderBy(
      Object.entries(groupedByCategory).map(([category, assetClasses]) => ({
        category,
        options: orderBy(
          assetClasses.map((ac) => ({
            id: ac.id,
            label: ac.name,
          })),
          [(ac) => ac.label],
          ["asc"]
        ),
      })),
      [(cat) => cat.category],
      ["asc"]
    );
  }, [currentAssetClassId, state.modelTable]);

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text className={styles.title} variant="title2">
        Assign new asset class
      </Text>

      <Text className={styles.text} variant="body" color="textSubtle">
        Select a new asset class for the holding
      </Text>

      <div className={styles.elementToAssign}>
        <div className={styles.smallBox} />

        <Text className={styles.text2} variant="body" color="text">
          {element.type === "Security" ? element.ticker : element.name}
        </Text>

        {element.type === "Security" && (
          <Text variant="body" color="textSubtle">
            {element.description}
          </Text>
        )}
      </div>

      <Text className={styles.label} variant="body" color="textSubtle">
        Asset Class
      </Text>

      <BaseDropdown
        placeholder={"No assigned asset class"}
        curItemId={assetClassToAssign?.id}
        curItemLabel={assetClassToAssign?.label}
        options={undefined}
        categoryWithOptions={assetClassesInModel}
        onSelect={onSelectAssetClassToAssign}
        width={470}
        height={280}
        customDropdownBaseClassName={styles.assetClassToAssignDropdown}
      />

      <Stack
        spacing={2}
        direction={"row"}
        marginTop={"40px"}
        justifyContent={"flex-end"}
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="outlined"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Save"
          buttonType="primary"
          variant="contained"
          disabled={assetClassToAssign?.id === undefined}
          onClick={onSave}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default ReAssignAssetClassModal;
